"use client";

import { useCurrentUser } from "@/hooks/use-current-user";
import { intercomContext } from "@/hooks/use-intercom";
import { afterPageLoad } from "@/lib/after-page-load";
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

type Messenger = typeof import("@intercom/messenger-js-sdk");
const INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

export function IntercomProvider({ children }: PropsWithChildren) {
  const { user, loaded } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isBooted, setIsBooted] = useState(false);
  const sdk = useRef<Promise<Messenger> | null>(null);
  const identity = useRef<string | null | undefined>(undefined);
  const registered = useRef(false);

  const boot = useCallback(async (): Promise<Messenger | null> => {
    if (!loaded || !INTERCOM_APP_ID || process.env.NODE_ENV !== "production")
      return null;
    try {
      sdk.current ??= import("@intercom/messenger-js-sdk");
      const messenger = await sdk.current;
      const nextIdentity = user?.id ?? null;
      if (identity.current !== nextIdentity) {
        const attributes = user
          ? {
              user_id: user.id,
              name: user.name ?? user.email,
              email: user.email,
              created_at: user.createdAt.getTime(),
            }
          : {};
        if (identity.current === null && user) messenger.update(attributes);
        else
          messenger.default({
            app_id: INTERCOM_APP_ID,
            hide_default_launcher: true,
            ...attributes,
          });
        identity.current = nextIdentity;
        setIsBooted(true);
      }
      if (!registered.current) {
        messenger.onHide(() => setIsOpen(false));
        messenger.onShow(() => setIsOpen(true));
        registered.current = true;
      }
      return messenger;
    } catch {
      sdk.current = null;
      return null;
    }
  }, [user, loaded]);

  useEffect(() => {
    if (isBooted) {
      void boot();
      return;
    }
    // Public pages use our own launcher, so download the messenger when requested.
    if (!user) return;
    return afterPageLoad(() => {
      void boot();
    });
  }, [boot, isBooted, user]);

  function show() {
    void boot().then((messenger) => messenger?.show());
  }
  function hide() {
    void boot().then((messenger) => messenger?.hide());
  }
  function toggle() {
    if (isOpen) hide();
    else show();
  }
  function showNewMessage(message: string) {
    void boot().then((messenger) => messenger?.showNewMessage(message));
  }

  return (
    <intercomContext.Provider
      value={{ show, hide, toggle, showNewMessage, isBooted }}
    >
      {children}
    </intercomContext.Provider>
  );
}
